import { useAtom } from "jotai";
import { backendDataAtom } from "../atoms/backendDataAtom.js";
import {
  backendGet,
  backendPatch,
  backendPost,
} from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";

export function useClaimActions() {
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  const state = backendData?.data
    ? backendData
    : {
        data: {},
        relations: {},
        meta: {},
      };

  const setState = (data) => {
    setBackendData((prev) => ({
      ...prev,
      data: data,
    }));
  };

  /**
   * Schaden nach ID suchen und im globalen Zustand speichern
   *
   * @param id
   * @param props
   * @returns {Promise<*|null>}
   */
  const findById = (id, props) => {
    return backendGet(`data/schaden/${id}`, {
      ...props,
      setResult: (res) => {
        setBackendData({
          data: res,
          relations: {},
          id: id,
          type: "schaden",
        });
      },
      enqueueSnackbar,
    });
  };

  /**
   * Neuen Schaden erstellen und im globalen Zustand speichern
   *
   * @param claimData
   * @param options
   * @returns {Promise<void>}
   */
  const createClaim = async (claimData, options) => {
    const { label = "Schaden erstellen", setIsLoading = () => {} } =
      options || {};

    await backendPost(`data/schaden`, {
      label,
      toSend: claimData,
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        setBackendData({
          data: res,
          relations: {},
          id: res.id,
          type: "schaden",
        });
      },
    });
  };

  /**
   * Schaden aktualisieren
   *
   * @param claim_id
   * @param claimData
   * @param options
   * @returns {Promise<void>}
   */
  const updateClaim = async (claim_id, claimData, options) => {
    const { label = "Schaden aktualisieren", setIsLoading = () => {} } =
      options || {};

    await backendPatch(`data/schadens/${claim_id}`, {
      label,
      toSend: claimData,
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        const data = {
          ...claimData,
          ...res.data,
          formdata: backendData.data.formdata,
        };
        setState(data);
      },
    });
  };

  /**
   * Schaden Formulardaten aktualisieren
   *
   * @param claim_id
   * @param formdata
   * @param props
   * @returns {Promise<void>}
   */
  const updateClaimFormdata = async (claim_id, formdata, props) => {
    const {
      label = "Schaden Formulardaten aktualisieren",
      setIsLoading = () => {},
      setResult,
      ...restProps
    } = props || {};

    await backendPatch(`data/schadens/${claim_id}/formdata`, {
      ...restProps,
      label,
      toSend: formdata,
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        setState(res);
        if (setResult) {
          setResult(res);
        }
      },
    });
  };

  /**
   * Schaden Status aktualisieren
   *
   * @param claim_id
   * @param status
   * @param options
   * @returns {Promise<void>}
   */
  const updateClaimStatus = async (claim_id, status, options) => {
    const { label = "Schaden Status aktualisieren", setIsLoading = () => {} } =
      options || {};

    await backendPatch(`data/schadens/${claim_id}/status`, {
      label,
      toSend: { status },
      setIsLoading,
      enqueueSnackbar,
      setResult: (res) => {
        const data = {
          ...backendData.data,
          ...res,
        };

        setState(data);
      },
      // the backend resets the claim when the transition fails, so reload it
      onError: (error) => {
        enqueueSnackbar(
          error.response?.data?.message ||
            "Der Status konnte nicht geändert werden.",
          { variant: "error" }
        );
        findById(claim_id);
      },
    });
  };

  /**
   * Schaden mit mehreren Sparten in einen Schaden je Sparte aufteilen
   *
   * @param claim_id
   * @param sparten IDs der Katalog-Sparten, die zur Prüfung gemeldet werden
   * @param options
   * @returns {Promise<Object|null>} the claims of the group, or null on failure
   */
  const splitClaim = async (claim_id, sparten, options) => {
    const { label = "Schaden aufteilen", setIsLoading = () => {} } =
      options || {};

    return await backendPost(`data/schadens/${claim_id}/split`, {
      label,
      toSend: { sparten },
      setIsLoading,
      enqueueSnackbar,
    });
  };

  /**
   * Neues Element zu einer Relation hinzufügen
   *
   * Beispiel: pushRelationItem('anlagen', newAttachment);
   *
   * @param relationKey
   * @param relationItem
   */
  const pushRelationItem = (relationKey, relationItem) => {
    setBackendData((prev) => {
      const currentRelations = prev.data[relationKey] || [];
      return {
        ...prev,
        data: {
          ...prev.data,
          [relationKey]: [...currentRelations, relationItem],
        },
      };
    });
  };

  /**
   * Element aus einer Relation entfernen
   *
   * @param relationKey
   * @param itemId
   */
  const removeRelationItem = (relationKey, itemId) => {
    setBackendData((prev) => {
      const currentRelations = prev.data[relationKey] || [];
      return {
        ...prev,
        data: {
          ...prev.data,
          [relationKey]: currentRelations.filter((item) => item.id !== itemId),
        },
      };
    });
  };

  return {
    createClaim,
    updateClaim,
    updateClaimFormdata,
    updateClaimStatus,
    splitClaim,
    setState,
    state,
    pushRelationItem,
    removeRelationItem,
    findById,
  };
}
